import { fetcher } from '@/services/graphql/fetcher';
import { useAppStore } from '@/store';
import { PlaceOrderType } from '@/types';
import {
  fetchActiveOrderByTypeWithPickupDocument,
  fetchActiveOrderByTypeWithoutPickupDocument,
  fetchActivityCountdownByCartDocument,
  fetchAddItemToOrderByOrderTypeDocument,
  fetchAdjustOrderLineDocument,
  fetchApplyShoppingCreditsDocument,
  fetchCancelShoppingCreditsDocument,
  fetchCheckProductCanCheckoutDocument,
  fetchEligibleShippingMethodsByOrderTypeDocument,
  fetchFreeGiftByOrderDocument,
  fetchGetActiveCouponsDocumenet,
  fetchGetOrderIsIncludeMemberPriceDocument,
  fetchGetShoppingCartWithPickupDocument,
  fetchGetShoppingCartWithoutPickupDocument,
  fetchMarkUpByOrderDocument,
  fetchOrderPromotionResultDocument,
  fetchRegularOrderExtrasDocument,
  fetchRemoveAllOrderLinesByOrderTypeDocument,
  fetchRemoveOrderLineDocument,
  fetchSetOrderCustomFieldsDocument,
  fetchShoppingCartSettlementDocument,
  fetchUpdateShoppingCartSkuDocument,
  setShippingMethodByOrderTypeDocument,
  fetchCalculateMemberDiscountDifferenceDocument,
  fetchGetGuessYouLikeProductsDocument,
  fetchAddItemToOrderByGuessYouLikeDocument,
  fetchRemoveItemFromOrderByGuessYouLikeDocument,
  fetchCalculateTimeDocument,
  setOrderDeliveryExpressDocument,
  setOrderDeliveryPickupDocument,
} from '../graphql/docs';

/**
 * 获取购物车和预下单信息
 * @param isRemoveMarkUp
 * @returns
 */
export const fetchGetShoppingCart = async (
  isRemoveMarkUp: boolean = false,
  isUseMember: boolean = true,
  isUseShoppingCredit: boolean = true,
  isRemoveGuessLike: boolean = true
) => {
  const appStore = useAppStore();
  const enablePickup = appStore.channelConfig?.enablePickup ?? false;
  const document = enablePickup ? fetchGetShoppingCartWithPickupDocument : fetchGetShoppingCartWithoutPickupDocument;
  const res = await fetcher.query(document, {
    variables: {
      isRemoveMarkUp,
      isUseMember,
      isUseShoppingCredit,
      isRemoveGuessLike,
    },
  });
  return res.data.value?.getShoppingCart;
};

/**
 * 获取购物车的活跃订单信息
 */
export const fetchActiveOrderByShoppingTrolley = async (isRemoveMarkUp: boolean = false, isRemoveGuessLike: boolean = true) => {
  const appStore = useAppStore();
  const enablePickup = appStore.channelConfig?.enablePickup ?? false;
  const document = enablePickup ? fetchActiveOrderByTypeWithPickupDocument : fetchActiveOrderByTypeWithoutPickupDocument;
  const res = await fetcher.query(document, {
    variables: {
      type: PlaceOrderType.shoppingTrolley,
      isRemoveMarkUp,
      isRemoveGuessLike,
    },
  });
  return res.data.value?.getActiveOrderByType;
};

/**
 * 加入购物车
 * @param variables - 请求参数{productVariantId, quantity}
 * @returns
 */
export const fetchAddItemToOrderByShoppingTrolley = async (variables: AnyObject) => {
  variables = { ...variables, type: PlaceOrderType.shoppingTrolley };
  const res = await fetcher.mutation(fetchAddItemToOrderByOrderTypeDocument, variables);
  return res.data?.addItemToOrderByOrderType;
};

/**
 * 加入购物车 - 预下单
 * @param variables - 请求参数{productVariantId, quantity}
 * @returns
 */
export const fetchAddItemToOrderByOutrightPurchase = async (variables: AnyObject) => {
  variables = { ...variables, type: PlaceOrderType.outrightPurchase };
  const res = await fetcher.mutation(fetchAddItemToOrderByOrderTypeDocument, variables);
  return res.data?.addItemToOrderByOrderType;
};

/**
 * 添加至订单 - 直接购买
 * @param variables - {productVariantId, quantity}
 */
export const fetchAddItemToOrderByRegularOrder = async (variables: AnyObject) => {
  variables = { ...variables, type: PlaceOrderType.regularOrder };
  const res = await fetcher.mutation(fetchAddItemToOrderByOrderTypeDocument, variables);
  return res.data?.addItemToOrderByOrderType;
};

/**
 * 积分商品兑换
 * @param variables
 * @returns
 */
export const fetchAddItemToOrderByPointsExchange = async (variables: AnyObject) => {
  variables = { ...variables, type: PlaceOrderType.regularOrder, buyType: 'pointsExchange' };
  const res = await fetcher.mutation(fetchAddItemToOrderByOrderTypeDocument, variables);
  return res.data?.addItemToOrderByOrderType;
};

/**
 * 根据id删除购物车 - 单选
 * @param id - 订单购物车id
 */
export const fetchRemoveOrderLineByShoppingTrolley = async (orderLineIds: string[]) => {
  const res = await fetcher.mutation(fetchRemoveOrderLineDocument, {
    orderLineIds,
    type: PlaceOrderType.shoppingTrolley,
  });
  return res.data?.removeOrderLineByOrderType;
};

/**
 * 根据id修改购物车项的数量
 * @param orderLineId - 订单购物车id
 * @param count - 修改的数量
 */
export const fetchModifyCartItemCountByShoppingTrolley = async (
  orderLineId: string,
  count: number
) => {
  const res = await fetcher.mutation(fetchAdjustOrderLineDocument, {
    orderLineId,
    quantity: count,
    type: PlaceOrderType.shoppingTrolley,
  });
  return res.data;
};

/**
 * 删除全部购物车订单
 */
export const fetchRemoveAllOrderByShoppingTrolley = async () => {
  const res = await fetcher.mutation(fetchRemoveAllOrderLinesByOrderTypeDocument, {
    type: PlaceOrderType.shoppingTrolley,
  });
  return res.data?.removeAllOrderLinesByOrderType;
};

/**
 * 购物车结算 - 预下单
 */
export const fetchSettlementShoppingCart = async (input: AnyObject) => {
  const res = await fetcher.mutation(fetchShoppingCartSettlementDocument, {
    input,
  });
  return res.data?.shoppingCartSettlement;
};

/**
 * 修改购物车规格、数量
 * @param variables - 请求参数
 * @returns
 */
export const fetchUpdateShoppingCartSku = async (variables: AnyObject) => {
  const res = await fetcher.mutation(fetchUpdateShoppingCartSkuDocument, variables);
  return res.data?.updateShoppingCartSku;
};

/**
 * 获取购物车优惠活动信息
 * @param orderIds - [购物车id, 预下单id]
 */
export const fetchOrderPromotionResult = async (orderIds: string[]) => {
  const res = await fetcher.query(fetchOrderPromotionResultDocument, {
    variables: {
      orderIds,
    },
  });
  return res.data.value?.orderPromotionResult;
};

/**
 * 获取直接购买的活跃订单信息
 */
export const fetchActiveOrderByRegularOrder = async () => {
  const appStore = useAppStore();
  const enablePickup = appStore.channelConfig?.enablePickup ?? false;
  const document = enablePickup ? fetchActiveOrderByTypeWithPickupDocument : fetchActiveOrderByTypeWithoutPickupDocument;
  const res = await fetcher.query(document, {
    variables: {
      type: PlaceOrderType.regularOrder,
    },
  });
  return res.data.value?.getActiveOrderByType;
};

/**
 * 获取发货方式 - 直接下单
 */
export const fetchEligibleShippingMethodsByRegularOrder = async () => {
  const res = await fetcher.query(fetchEligibleShippingMethodsByOrderTypeDocument, {
    variables: {
      type: PlaceOrderType.regularOrder,
    },
  });
  return res.data.value?.eligibleShippingMethodsByOrderType;
};

/**
 * 获取发货方式 - 预下单
 */
export const fetchEligibleShippingMethodsByOutrightPurchase = async () => {
  const res = await fetcher.query(fetchEligibleShippingMethodsByOrderTypeDocument, {
    variables: {
      type: PlaceOrderType.outrightPurchase,
    },
  });
  return res.data.value?.eligibleShippingMethodsByOrderType;
};

/**
 * 设置发货方式 - 直接下单
 */
export const fetchSetShippingMethodByRegularOrder = async (shippingMethodId: string) => {
  const variables = {
    type: PlaceOrderType.regularOrder,
    shippingMethodId,
  };
  const res = await fetcher.mutation(setShippingMethodByOrderTypeDocument, variables);
  return res.data.setShippingMethodByOrderType;
};

/**
 * 设置发货方式 - 预下单
 */
export const fetchSetShippingMethodByOutrightPurchase = async (shippingMethodId: string) => {
  const variables = {
    type: PlaceOrderType.outrightPurchase,
    shippingMethodId,
  };
  const res = await fetcher.mutation(setShippingMethodByOrderTypeDocument, variables);
  return res.data.setShippingMethodByOrderType;
};

/**
 * 设置下单自定义字段
 */
export const fetchSetOrderCustomFields = async (variables: AnyObject) => {
  const res = await fetcher.mutation(fetchSetOrderCustomFieldsDocument, variables);
  return res.data.setOrderCustomFields;
};

/**
 * 查询当前订单中的商品是否参与了会员价活动
 * @param orderId - 订单id
 * @returns
 */
export const fetchGetOrderIsIncludeMemberPrice = async (orderId: string) => {
  const res = await fetcher.query(fetchGetOrderIsIncludeMemberPriceDocument, {
    variables: { orderId },
  });
  return res.data.value?.getOrderIsIncludeMemberPrice;
};

/**
 * 查询结算订单中的商品是否都是限购
 * @param skuIds - 结算订单的商品规格id数组
 * @returns
 */
export const fetchCheckProductCanCheckout = async (
  skuIds: string[],
  skuQuantities: { skuId: string; quantity: number }[]
) => {
  const res = await fetcher.query(fetchCheckProductCanCheckoutDocument, {
    variables: { skuIds, skuQuantities },
  });
  return res.data.value?.checkProductCanCheckout;
};

/**
 * 申请购物金抵扣
 */
export const fetchApplyShoppingCredits = async (orderId: string) => {
  const res = await fetcher.mutation(fetchApplyShoppingCreditsDocument, { orderId });
  return res.data?.applyShoppingCredits;
};

/**
 * 取消购物金抵扣
 */
export const fetchCancelShoppingCredits = async (orderId: string) => {
  const res = await fetcher.mutation(fetchCancelShoppingCreditsDocument, { orderId });
  return res.data?.cancelShoppingCredits;
};

/**
 * 查询购物车页面是否有活动倒计时
 * @returns
 */
export const fetchActivityCountdownByCart = async () => {
  const res = await fetcher.query(fetchActivityCountdownByCartDocument, {});
  return res.data.value?.activityCountdownByCart;
};

/**
 * 获取 可用/不可用 优惠券列表
 * @param orderId - 订单id
 */
export const fetchGetActiveCoupons = async (orderId: string) => {
  const res = await fetcher.query(fetchGetActiveCouponsDocumenet, {
    variables: {
      orderId,
    },
  });
  return res.data.value?.getActiveCoupons;
};

/**
 * 根据订单id查询换购商品
 * @param orderId - 订单id
 * @returns
 */
export const fetchMarkUpByOrder = async (orderId: string) => {
  const res = await fetcher.query(fetchMarkUpByOrderDocument, {
    variables: {
      orderId,
    },
  });
  return res.data.value?.markUpByOrder;
};

/**
 * 根据订单id查询赠品
 * @param orderId - 订单id
 * @returns
 */
export const fetchFreeGiftByOrderId = async (orderId: string) => {
  const res = await fetcher.query(fetchFreeGiftByOrderDocument, {
    variables: {
      orderId,
    },
  });
  return res.data.value?.freeGiftByOrder;
};

/**
 * 获取订单确认页附加数据（5合1）- 直接购买场景
 * 合并: orderPromotionResult + freeGiftByOrder + markUpByOrder + getActiveCoupons + getOrderIsIncludeMemberPrice
 * @param orderId - 订单id
 */
export const fetchRegularOrderExtras = async (orderId: string) => {
  const res = await fetcher.query(fetchRegularOrderExtrasDocument, {
    variables: {
      orderId,
    },
  });
  return res.data.value;
};

/**
 * 获取开通年卡会员后可以优惠多少结算金额
 * @param orderId - 订单id
 */
export const fetchCalculateMemberDiscountDifference = async (orderId: string) => {
  const res = await fetcher.query(fetchCalculateMemberDiscountDifferenceDocument, {
    variables: {
      orderId,
    },
  });
  return res.data.value?.calculateMemberDiscountDifference;
};

/**
 * 获取猜你喜欢商品
 * @param orderId - 订单id
 */
export const fetchGetGuessYouLikeProducts = async (orderId: string) => {
  const res = await fetcher.query(fetchGetGuessYouLikeProductsDocument, {
    variables: {
      orderId,
    },
  });
  return res.data.value?.getGuessYouLikeProducts;
};

/**
 * 订单加入猜你喜欢商品
 */
export const fetchAddItemToOrderByGuessYouLike = async (orderId: string, productVariantId: string) => {
  const res = await fetcher.mutation(fetchAddItemToOrderByGuessYouLikeDocument, {
    orderId,
    productVariantId
  });
  return res.data?.addItemToOrderByGuessYouLike;
};

/**
 * 订单移除猜你喜欢商品
 */
export const fetchRemoveItemFromOrderByGuessYouLike = async (orderId: string) => {
  const res = await fetcher.mutation(fetchRemoveItemFromOrderByGuessYouLikeDocument, {
    orderId,
  });
  return res.data?.removeItemFromOrderByGuessYouLike;
};


/**
 * 抽奖活动触发计算附加次数
 * @param lotteryActivityId - 抽奖活动id
 * @param source - 抽奖活动次数来源
 * @param sourceValue - 抽奖活动次数
 * @returns
 */
export const fetchCalculateTime = async (variables: AnyObject) => {
  const res = await fetcher.mutation(fetchCalculateTimeDocument, variables);
  return res.data?.calculateTime;
};

/**
 * 设置订单为快递配送
 * @param orderId - 订单id
 */
export const fetchSetOrderDeliveryExpress = async (orderId: string) => {
  const res = await fetcher.mutation(setOrderDeliveryExpressDocument, { orderId });
  return res.data?.setOrderDeliveryExpress;
};

/**
 * 设置订单为门店自提
 * @param orderId - 订单id
 */
export const fetchSetOrderDeliveryPickup = async (orderId: string) => {
  const res = await fetcher.mutation(setOrderDeliveryPickupDocument, { orderId });
  return res.data?.setOrderDeliveryPickup;
};
